import { DateTime } from 'luxon'

/** Fallback used everywhere the brand kit's timezone is null. Matches the
 *  server's `DEFAULT_TIMEZONE` in calendar.service.ts so client display and
 *  server bucketing always agree. */
export const DEFAULT_TIMEZONE = 'UTC'

/**
 * Resolve the browser-detected IANA timezone (e.g. `America/New_York`).
 * Returns `UTC` if the browser refuses to disclose one (very rare —
 * Intl.DateTimeFormat is universally supported in our target browsers, but
 * a hardened privacy extension can null it out).
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

/**
 * Resolve a wall-clock date+time in a given zone to a luxon DateTime.
 *
 * Invalid TZ → luxon returns an invalid DateTime; we fall back to UTC so we
 * never produce garbage (the lazy-capture flow overwrites the zone next
 * session). Shared by `buildScheduledInstant` and `isTimeOptionInPast` so the
 * "what instant does this wall clock map to" logic lives in exactly one place.
 */
function wallClockDateTime(date: string, time: string, timezone: string): DateTime {
  const [hour, minute] = time.split(':').map(Number)
  const [year, month, day] = date.split('-').map(Number)
  const dt = DateTime.fromObject(
    { year, month, day, hour, minute, second: 0, millisecond: 0 },
    { zone: timezone }
  )
  if (dt.isValid) return dt
  return DateTime.fromObject(
    { year, month, day, hour, minute, second: 0, millisecond: 0 },
    { zone: DEFAULT_TIMEZONE }
  )
}

/**
 * Build the ISO instant a scheduled post should fire at.
 *
 * Replaces the broken `${date}T${time}:00Z` pattern that stamped `Z` onto a
 * local-wall-clock date+time string — for a NYC user picking "9am tomorrow"
 * that previously persisted `09:00 UTC` (= 5am EDT, four hours early). Here
 * we anchor the wall clock to `timezone` and let luxon convert to UTC.
 *
 * @param date  YYYY-MM-DD (wall date in the business timezone)
 * @param time  HH:mm (wall time in the business timezone, 24h)
 * @param timezone  IANA zone the user chose for this brand. Pass the browser
 *   TZ as a fallback when the brand hasn't captured one yet.
 * @returns ISO 8601 instant with offset (e.g. `2026-04-15T13:00:00.000-04:00`).
 *   Server `@IsISO8601()` accepts the offset form unchanged.
 */
export function buildScheduledInstant(date: string, time: string, timezone: string): string {
  return wallClockDateTime(date, time, timezone).toISO()!
}

/**
 * True when the wall-clock `date`+`time` (interpreted in `timezone`) is at or
 * before `nowMs`. Used to drop time-of-day options that have already passed so
 * the scheduler never offers a slot in the past — for a future date this is
 * always false, for today it's true for every hour up to the current one.
 * `nowMs` is injectable for deterministic tests.
 */
export function isTimeOptionInPast(
  date: string,
  time: string,
  timezone: string,
  nowMs: number = Date.now()
): boolean {
  return wallClockDateTime(date, time, timezone).toMillis() <= nowMs
}

/** Today's wall date (YYYY-MM-DD) in the business timezone. */
export function todayInTz(timezone: string, nowMs: number = Date.now()): string {
  const dt = DateTime.fromMillis(nowMs, { zone: timezone })
  if (!dt.isValid)
    return DateTime.fromMillis(nowMs, { zone: DEFAULT_TIMEZONE }).toFormat('yyyy-LL-dd')
  return dt.toFormat('yyyy-LL-dd')
}

/**
 * Tomorrow's wall date (YYYY-MM-DD) in the business timezone. Uses calendar
 * arithmetic (`plus({ days: 1 })`), NOT `nowMs + 24h` — on a DST spring-forward
 * day the latter overshoots into the day after (24h of real time spans a 23h
 * calendar day), silently skipping a day.
 */
export function tomorrowInTz(timezone: string, nowMs: number = Date.now()): string {
  const dt = DateTime.fromMillis(nowMs, { zone: timezone })
  const base = dt.isValid ? dt : DateTime.fromMillis(nowMs, { zone: DEFAULT_TIMEZONE })
  return base.plus({ days: 1 }).toFormat('yyyy-LL-dd')
}

/**
 * Format an ISO instant in the business timezone using a luxon format token
 * (e.g. `h:mm a`, `EEEE, MMM d`). Used by the calendar to render times the
 * way the user expects regardless of which browser they opened it from.
 */
export function formatInBusinessTz(iso: string, timezone: string, fmt: string): string {
  const dt = DateTime.fromISO(iso, { zone: timezone })
  if (!dt.isValid) return ''
  return dt.toFormat(fmt)
}

/**
 * Bucket an ISO instant into a YYYY-MM-DD wall date in the business
 * timezone. Pairs with the server's `formatDate(date, tz)` so calendar
 * client + server agree on which day cell a post belongs to (the bug was
 * "post lands on the wrong day near midnight" — they used different zones).
 */
export function bucketDateInTz(iso: string, timezone: string): string {
  const dt = DateTime.fromISO(iso, { zone: timezone })
  if (!dt.isValid) return iso.slice(0, 10)
  return dt.toFormat('yyyy-LL-dd')
}

/**
 * Canonical human label for a scheduled/published instant, rendered in the
 * business timezone — e.g. `Jun 22 at 9:00 AM`. One formatter for every
 * scheduled/published-time display (composer CTA, toast, cockpit, idea cards)
 * so they never disagree on the wall clock the way browser-local/UTC variants
 * did. Returns '' for an unparseable instant (matches `formatInBusinessTz`).
 */
export function formatScheduledLabel(iso: string, timezone: string): string {
  return formatInBusinessTz(iso, timezone, "LLL d 'at' h:mm a")
}

/**
 * Split a persisted ISO instant into the `{ date, time }` wall-clock parts of a
 * given zone, shaped for the date/time pickers (`<input type=date>` wants
 * `yyyy-LL-dd`; the time `<select>` options are `HH:mm`). Inverse of
 * `buildScheduledInstant` — seed a picker with this so editing an existing
 * schedule opens on the same wall clock the label shows. Invalid TZ falls back
 * to UTC, mirroring `wallClockDateTime`.
 */
export function splitIsoInTz(iso: string, timezone: string): { date: string; time: string } {
  let dt = DateTime.fromISO(iso, { zone: timezone })
  if (!dt.isValid) dt = DateTime.fromISO(iso, { zone: DEFAULT_TIMEZONE })
  return { date: dt.toFormat('yyyy-LL-dd'), time: dt.toFormat('HH:mm') }
}
